// Guia "Como usar" (texto 08, C3): mini animação de cada gesto, desenhada no próprio app (sem prints).
// Um dedo estilizado (bolinha) faz o gesto sobre um cartão de exemplo. O MESMO componente é usado em todo lugar.
// Com "Reduzir movimento" ligado no aparelho, mostra o desenho parado no meio do gesto.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AccessibilityInfo, Animated, Easing, Platform, Text, View } from 'react-native';
import { C, alpha } from '../theme';
import { t } from '../../lib/i18n';
import type { GestoId } from '../../lib/guia/tipos';

const NATIVO = Platform.OS !== 'web';
const NOMES: Record<GestoId, string> = {
  'arrastar-direita': 'Arrastar para a direita', 'arrastar-esquerda': 'Arrastar para a esquerda', 'segurar-arrastar': 'Segurar e arrastar',
  'segurar-soltar': 'Segurar e soltar', pinca: 'Pinça', 'segurar-folha': 'Segurar na folha vazia', 'segurar-bolinha': 'Segurar a bolinha de cor', tocar: 'Tocar',
};
export const nomeGesto = (g: GestoId) => t(NOMES[g] ?? g);

function useReduzir() {
  const [r, setR] = useState(false);
  useEffect(() => {
    let vivo = true;
    AccessibilityInfo.isReduceMotionEnabled?.().then(v => { if (vivo) setR(!!v); }).catch(() => {});
    const s = AccessibilityInfo.addEventListener?.('reduceMotionChanged', (v: boolean) => setR(!!v));
    return () => { vivo = false; s?.remove?.(); };
  }, []);
  return r;
}

/** Tempo de cada gesto (ms) por volta. */
const DUR: Record<GestoId, number> = {
  'arrastar-direita': 1800, 'arrastar-esquerda': 1800, 'segurar-arrastar': 2400, 'segurar-soltar': 2000, pinca: 2000,
  'segurar-folha': 2400, 'segurar-bolinha': 2200, tocar: 1200,
};

/** Mini animação do gesto. tam: 'p' (ao lado de um passo) ou 'm' (bloco de gestos). */
/** tam 'g' (texto 29): maior e com espaço dos lados, para o cartão deslizar sem ser cortado. */
export function Gesto({ id, tam = 'p', cor }: { id: GestoId; tam?: 'p' | 'm' | 'g'; cor?: string }) {
  const reduzir = useReduzir();
  const p = useRef(new Animated.Value(reduzir ? 0.55 : 0)).current;
  useEffect(() => {
    if (reduzir) { p.setValue(0.55); return; }
    p.setValue(0);
    const a = Animated.loop(Animated.sequence([
      Animated.timing(p, { toValue: 1, duration: DUR[id] ?? 1800, easing: Easing.inOut(Easing.quad), useNativeDriver: NATIVO }),
      Animated.delay(450),
    ]));
    a.start();
    return () => a.stop();
  }, [id, reduzir, p]);

  const k = tam === 'g' ? 1.3 : tam === 'm' ? 1.35 : 1;
  const W = Math.round(112 * k), H = Math.round(64 * k);
  const destaque = cor ?? '#8B5CF6';
  const r = (a: number[], b: number[]) => p.interpolate({ inputRange: a, outputRange: b, extrapolate: 'clamp' });
  const dedo = 18 * k;

  // cartão de exemplo: duas linhas de "texto"
  const cartao = (estilo?: object, extra?: ReactNode) => (
    <Animated.View style={[{ position: 'absolute', left: 10 * k, right: 10 * k, top: 14 * k, height: 34 * k, borderRadius: 8 * k, backgroundColor: C.card,
      borderWidth: 1, borderColor: C.line, paddingHorizontal: 8 * k, justifyContent: 'center', gap: 4 * k }, estilo]}>
      <View style={{ height: 5 * k, width: '70%', borderRadius: 3, backgroundColor: C.sub, opacity: 0.6 }} />
      <View style={{ height: 4 * k, width: '45%', borderRadius: 3, backgroundColor: C.mute, opacity: 0.6 }} />
      {extra}
    </Animated.View>
  );
  const Dedo = ({ x, y, s = 1, op = 1 }: { x: Animated.AnimatedInterpolation<number> | number; y: Animated.AnimatedInterpolation<number> | number; s?: Animated.AnimatedInterpolation<number> | number; op?: Animated.AnimatedInterpolation<number> | number }) => (
    <Animated.View pointerEvents="none" style={{ position: 'absolute', left: 0, top: 0, width: dedo, height: dedo, borderRadius: dedo / 2,
      backgroundColor: alpha('#FFFFFF', 0.85), borderWidth: 2, borderColor: destaque, opacity: op,
      transform: [{ translateX: x as number }, { translateY: y as number }, { scale: s as number }],
      shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 3 }} />
  );
  // anel que cresce = "segurar"
  const Anel = ({ x, y, s, op }: { x: number; y: number; s: Animated.AnimatedInterpolation<number>; op: Animated.AnimatedInterpolation<number> }) => (
    <Animated.View pointerEvents="none" style={{ position: 'absolute', left: x - dedo / 2, top: y - dedo / 2, width: dedo * 2, height: dedo * 2, borderRadius: dedo,
      borderWidth: 2, borderColor: destaque, opacity: op, transform: [{ scale: s }] }} />
  );
  const cx = W / 2 - dedo / 2, cy = 14 * k + 17 * k - dedo / 2;

  let corpo: ReactNode = null;
  switch (id) {
    case 'arrastar-direita':
    case 'arrastar-esquerda': {
      const dir = id === 'arrastar-direita' ? 1 : -1;
      const dx = r([0, 0.15, 0.7, 0.85, 1], [0, 0, 46 * k * dir, 46 * k * dir, 0]);
      corpo = (<>
        <View style={{ position: 'absolute', left: 10 * k, right: 10 * k, top: 14 * k, height: 34 * k, borderRadius: 8 * k, flexDirection: 'row',
          justifyContent: dir > 0 ? 'flex-start' : 'flex-end', alignItems: 'center', paddingHorizontal: 8 * k,
          backgroundColor: dir > 0 ? alpha(C.danger, 0.85) : alpha(C.warn, 0.85) }}>
          <Text style={{ fontSize: 13 * k }}>{dir > 0 ? '🗑️' : '🗄️'}</Text>
        </View>
        {cartao({ transform: [{ translateX: dx }] })}
        <Dedo x={Animated.add(dx, cx - 20 * k * dir) as any} y={cy} op={r([0, 0.08, 0.8, 0.9], [0, 1, 1, 0])} />
      </>);
      break;
    }
    case 'segurar-arrastar': {
      const dy = r([0, 0.35, 0.75, 0.88, 1], [0, 0, 22 * k, 22 * k, 0]);
      const sobe = r([0, 0.35, 0.75, 0.88, 1], [0, 0, -22 * k, -22 * k, 0]);
      corpo = (<>
        <Animated.View style={{ position: 'absolute', left: 10 * k, right: 10 * k, top: 36 * k, height: 20 * k, borderRadius: 6 * k, backgroundColor: C.card2, transform: [{ translateY: sobe }] }} />
        <Animated.View style={{ position: 'absolute', left: 10 * k, right: 10 * k, top: 8 * k, height: 20 * k, borderRadius: 6 * k, backgroundColor: C.card,
          borderWidth: 1.5, borderColor: destaque, transform: [{ translateY: dy }, { scale: r([0, 0.3, 0.35, 0.8, 0.88], [1, 1, 1.05, 1.05, 1]) }],
          justifyContent: 'center', paddingHorizontal: 8 * k }}>
          <View style={{ height: 4 * k, width: '60%', borderRadius: 3, backgroundColor: C.sub, opacity: 0.6 }} />
        </Animated.View>
        <Anel x={W / 2} y={18 * k} s={r([0.05, 0.33, 0.36], [0.4, 1, 1])} op={r([0.05, 0.3, 0.36, 0.4], [0, 0.9, 0.9, 0])} />
        <Dedo x={W / 2 - dedo / 2} y={Animated.add(dy, 18 * k - dedo / 2) as any} op={r([0, 0.05, 0.85, 0.92], [0, 1, 1, 0])} />
      </>);
      break;
    }
    case 'segurar-soltar': {
      const sel = r([0, 0.45, 0.5, 1], [0, 0, 1, 1]);
      corpo = (<>
        {cartao({ borderColor: destaque, borderWidth: 1 }, (
          <Animated.View style={{ position: 'absolute', right: 6 * k, top: 8 * k, width: 16 * k, height: 16 * k, borderRadius: 8 * k, backgroundColor: destaque,
            alignItems: 'center', justifyContent: 'center', opacity: sel, transform: [{ scale: sel }] }}>
            <Text style={{ color: '#fff', fontSize: 10 * k, fontWeight: '800' }}>✓</Text>
          </Animated.View>))}
        <Anel x={W / 2} y={cy + dedo / 2} s={r([0.05, 0.45, 0.5], [0.4, 1, 1])} op={r([0.05, 0.4, 0.5, 0.55], [0, 0.9, 0.9, 0])} />
        <Dedo x={cx} y={cy} op={r([0, 0.05, 0.55, 0.65], [0, 1, 1, 0])} />
      </>);
      break;
    }
    case 'pinca': {
      const abre = r([0, 0.2, 0.7, 0.85, 1], [0, 0, 1, 1, 0]);
      const esc = r([0, 0.2, 0.7, 0.85, 1], [1, 1, 1.35, 1.35, 1]);
      const d = Animated.multiply(abre, 22 * k);
      corpo = (<>
        {cartao({ transform: [{ scale: esc }] })}
        <Dedo x={Animated.subtract(cx - 8 * k, d) as any} y={Animated.subtract(cy + 4 * k, Animated.multiply(abre, 8 * k)) as any} op={r([0, 0.1, 0.75, 0.85], [0, 1, 1, 0])} />
        <Dedo x={Animated.add(d, cx + 8 * k) as any} y={Animated.add(Animated.multiply(abre, 8 * k), cy - 4 * k) as any} op={r([0, 0.1, 0.75, 0.85], [0, 1, 1, 0])} />
      </>);
      break;
    }
    case 'segurar-folha': {
      const move = r([0, 0.45, 0.8, 0.9, 1], [0, 0, 1, 1, 0]);
      const dx = Animated.multiply(move, 16 * k), dy = Animated.multiply(move, 8 * k);
      corpo = (<>
        <Animated.View style={{ position: 'absolute', left: 18 * k, right: 18 * k, top: 4 * k, bottom: 4 * k, borderRadius: 4 * k, backgroundColor: '#FFFFFF',
          borderWidth: 1, borderColor: C.line, transform: [{ translateX: dx }, { translateY: dy }], paddingTop: 10 * k, gap: 9 * k, paddingHorizontal: 6 * k }}>
          {[0, 1, 2, 3].map(i => <View key={i} style={{ height: 1, backgroundColor: '#BFD4EA' }} />)}
        </Animated.View>
        <Animated.Text style={{ position: 'absolute', left: W / 2 + 10 * k, top: 4 * k, fontSize: 14 * k, color: destaque, fontWeight: '800',
          opacity: r([0, 0.4, 0.45, 0.85, 0.9], [0, 0, 1, 1, 0]) }}>✥</Animated.Text>
        <Anel x={W / 2} y={H / 2} s={r([0.05, 0.4, 0.45], [0.4, 1, 1])} op={r([0.05, 0.35, 0.45, 0.5], [0, 0.9, 0.9, 0])} />
        <Dedo x={Animated.add(dx, W / 2 - dedo / 2) as any} y={Animated.add(dy, H / 2 - dedo / 2) as any} op={r([0, 0.05, 0.82, 0.9], [0, 1, 1, 0])} />
      </>);
      break;
    }
    case 'segurar-bolinha': {
      const cores = ['#111827', '#2563EB', '#DC2626', '#16A34A'];
      const bx = (i: number) => 14 * k + i * 22 * k;
      const by = 34 * k;
      corpo = (<>
        {cores.map((c, i) => (
          <Animated.View key={c} style={{ position: 'absolute', left: bx(i), top: by, width: 16 * k, height: 16 * k, borderRadius: 8 * k,
            backgroundColor: i === 1 ? undefined : c, borderWidth: i === 1 ? 2 : 0, borderColor: C.text }}>
            {i === 1 && <Animated.View style={{ flex: 1, borderRadius: 8 * k, backgroundColor: c, opacity: r([0, 0.55, 0.6], [1, 1, 0]) }} />}
            {i === 1 && <Animated.View style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, borderRadius: 8 * k, backgroundColor: '#F59E0B', opacity: r([0, 0.6, 0.65], [0, 0, 1]) }} />}
          </Animated.View>
        ))}
        <Animated.View style={{ position: 'absolute', left: 8 * k, top: 4 * k, flexDirection: 'row', gap: 3 * k, padding: 3 * k, borderRadius: 6 * k, backgroundColor: C.card,
          borderWidth: 1, borderColor: C.line, opacity: r([0, 0.4, 0.45, 0.6, 0.65], [0, 0, 1, 1, 0]) }}>
          {['#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#84CC16'].map(c => <View key={c} style={{ width: 11 * k, height: 11 * k, borderRadius: 6 * k, backgroundColor: c }} />)}
        </Animated.View>
        <Anel x={bx(1) + 8 * k} y={by + 8 * k} s={r([0.05, 0.4, 0.45], [0.4, 1, 1])} op={r([0.05, 0.35, 0.45, 0.5], [0, 0.9, 0.9, 0])} />
        <Dedo x={bx(1) + 8 * k - dedo / 2} y={Animated.add(r([0, 0.45, 0.55, 0.6, 1], [0, 0, -(by - 6 * k), -(by - 6 * k), -(by - 6 * k)]), by + 8 * k - dedo / 2) as any}
          op={r([0, 0.05, 0.62, 0.7], [0, 1, 1, 0])} />
      </>);
      break;
    }
    case 'tocar':
    default: {
      corpo = (<>
        {cartao(undefined, (
          <Animated.View pointerEvents="none" style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, borderRadius: 8 * k, backgroundColor: alpha(destaque, 0.25),
            opacity: r([0, 0.4, 0.5, 0.8], [0, 0, 1, 0]) }} />))}
        <Dedo x={cx} y={cy} s={r([0, 0.35, 0.45, 0.55, 1], [1, 1, 0.75, 1, 1])} op={r([0, 0.1, 0.7, 0.85], [0, 1, 1, 0])} />
      </>);
    }
  }

  if (tam === 'g') {
    // o cartão anda 46 para o lado: a moldura tem essa folga dos dois lados e não corta nada
    const folga = 50 * k;
    return (
      <View accessible accessibilityLabel={nomeGesto(id)} style={{ width: W + 2 * folga, height: H + 8, borderRadius: 10 * k, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}>
        <View style={{ width: W, height: H }}>{corpo}</View>
      </View>
    );
  }
  return (
    <View accessible accessibilityLabel={nomeGesto(id)} style={{ width: W, height: H, borderRadius: 10 * k, backgroundColor: C.card2, overflow: 'hidden' }}>
      {corpo}
    </View>
  );
}
